import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    FileText, Store,
    Calendar,
    Loader2,
    AlertTriangle,
    Info,
    TrendingUp,
    CreditCard,
    Wallet,
    ShoppingBag,
    Printer,
    RefreshCw,
    Search,
    ChevronDown,
    Banknote,
} from 'lucide-react';
import Card from '../components/ui/Card';
import Input from '../components/ui/Input';
import Button from '../components/ui/Button';
import { useToast } from '../hooks/useToast';
import { fetchEndOfDay, fetchStores, fetchStoresWhereStaffAssigned } from '../apiHelper';
import SearchableSelectBox from '../components/ui/SearchableSelectBox';
import ThermalReceipt from './ThermalReceipt';
import Modal from '../components/ui/Modal';
import Dropdown from '../components/ui/Dropdown';
import SummaryCard from '../components/ui/SummaryCard';
// import TrialModal from '../components/ui/TrialModal';

const EndOfDaySalesReport = () => {
    const navigate = useNavigate();
    const { showToast } = useToast();

    const [user, setUser] = useState(null);
    const [allOrders, setAllOrders] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const [searchTerm, setSearchTerm] = useState('');
    const [selectedCreator, setSelectedCreator] = useState('all');
    const [selectedCustomer, setSelectedCustomer] = useState('all');

    const [selectedIds, setSelectedIds] = useState([]);
    const [selectedOrder, setSelectedOrder] = useState(false);
    const [isModalOpen, setIsModalOpen] = useState(false);

    const [showTrialModal, setShowTrialModal] = React.useState(true);
    const [reportDate, setReportDate] = useState(new Date().toISOString().split('T')[0]);

    const [stores, setStores] = useState([]);
    const [selectedStoreId, setSelectedStoreId] = useState(null);

    const toggleOrder = (id) => {
        setSelectedIds(prev =>
            prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
        );
    };

    const handleViewReceipt = (order) => {
        setSelectedOrder(order);
        setIsModalOpen(true);
    };

    const toggleAll = () => {
        if (selectedIds.length === filteredOrders.length) {
            setSelectedIds([]);
        } else {
            setSelectedIds(filteredOrders.map(o => o.orders_id));
        }
    };

    const getSettlementData = (orders) => {
        const report = {
            totalCollected: 0,
            totalCredit: 0,
            totalSales: 0,
            itemSummary: new Map(),
            cashiers: new Set(),
            store: orders[0]?.store?.identifier?.toUpperCase() || 'STORE',
            currency: orders[0]?.order_currency?.setcnote || '₦',
        };

        orders.forEach(o => {
            const coll = Number(o.summary_collected || 0);
            const cred = Number(o.summary_credit || 0);

            report.totalCollected += coll;
            report.totalCredit += cred;
            report.totalSales += (coll + cred);

            if (o.creator?.user?.first_name) report.cashiers.add(o.creator.user.first_name);

            o.orderitems.forEach(item => {
                const id = item.catentry_id;
                const existing = report.itemSummary.get(id) || { name: item.catentry.productname, qty: 0, total: 0 };
                report.itemSummary.set(id, {
                    ...existing,
                    qty: existing.qty + item.quantity,
                    total: existing.total + item.totalproduct
                });
            });
        });
        return report;
    };

    const handlePDFPreview = (orders) => {
        const data = getSettlementData(orders);
        const cashierDisp = Array.from(data.cashiers).join(', ');
        const printWindow = window.open('', '_blank');

        const sortedItems = Array.from(data.itemSummary.values())
            .sort((a, b) => b.total - a.total);

        const html = `
    <html>
        <head>
            <style>
                body { font-family: sans-serif; padding: 40px; color: #1e293b; }
                .brand { text-align: center; border-bottom: 4px solid #2563eb; padding-bottom: 10px; }
                .stats-grid { display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 20px; margin: 20px 0; }
                .stat-card { border: 1px solid #e2e8f0; padding: 15px; border-radius: 8px; text-align: center; }
                .stat-val { font-size: 20px; font-weight: bold; color: #2563eb; }
                table { width: 100%; border-collapse: collapse; margin: 20px 0; }
                th { background: #f8fafc; text-align: left; padding: 12px; border-bottom: 2px solid #cbd5e1; }
                td { padding: 12px; border-bottom: 1px solid #f1f5f9; }
                .section-title { margin: 32px 0 12px; font-size: 1.2rem; font-weight: bold; color: #334155; }
                .footer { margin-top: 40px; border-top: 1px solid #cbd5e1; padding-top: 20px; font-size: 12px; color: #64748b; }
            </style>
        </head>
        <body>
            <div class="brand">
                <h1>${data.store}</h1>
                <p>End of Day Reconciled Sales Report</p>
                <p style="font-size:0.9rem; color:#64748b;">${new Date().toLocaleString()}</p>
            </div>

            <div class="stats-grid">
                <div class="stat-card"><div>Actual Cash/Bank</div><div class="stat-val">${data.currency}${data.totalCollected.toLocaleString()}</div></div>
                <div class="stat-card"><div>Credit/Outstanding</div><div class="stat-val">${data.currency}${data.totalCredit.toLocaleString()}</div></div>
                <div class="stat-card"><div>Gross Sales</div><div class="stat-val">${data.currency}${data.totalSales.toLocaleString()}</div></div>
            </div>

            <div class="section-title">Top Selling Items Summary</div>
            <table>
                <thead>
                    <tr>
                        <th>Product</th>
                        <th style="text-align:right">Qty</th>
                        <th style="text-align:right">Total Sales</th>
                    </tr>
                </thead>
                <tbody>
                    ${sortedItems.map(item => `
                        <tr>
                            <td>${item.name}</td>
                            <td style="text-align:right">${item.qty.toLocaleString()}</td>
                            <td style="text-align:right">${data.currency}${item.total.toLocaleString()}</td>
                        </tr>
                    `).join('')}
                </tbody>
            </table>

            <div class="section-title">Order Settlement Breakdown</div>
            <table>
                <thead>
                    <tr><th>Order ID</th><th>Customer</th><th>Paid (Collected)</th><th>Debt (Credit)</th><th>Total</th></tr>
                </thead>
                <tbody>
                    ${orders.map(o => `
                        <tr>
                            <td>#${o.orders_id}</td>
                            <td>${o.customer?.user?.first_name || ''} ${o.customer?.user?.last_name || ''}</td>
                            <td>${data.currency}${Number(o.summary_collected || 0).toLocaleString()}</td>
                            <td>${data.currency}${Number(o.summary_credit || 0).toLocaleString()}</td>
                            <td>${data.currency}${o.totalproduct.toLocaleString()}</td>
                        </tr>
                    `).join('')}
                </tbody>
            </table>

            <div class="footer">
                <p>Cashier(s) on Duty: ${cashierDisp || '—'}</p>
                <p>Generated: ${new Date().toLocaleString()} • ${orders.length} order${orders.length === 1 ? '' : 's'}</p>
            </div>

            <script>window.onload = () => setTimeout(() => window.print(), 300);</script>
        </body>
    </html>`;

        printWindow.document.write(html);
        printWindow.document.close();
    };

    const printThermalRawBT = (orders) => {
        if (orders.length === 0) return showToast("No orders selected", "warning");

        const currency = orders[0]?.order_currency?.setcnote || '₦';
        const storeName = orders[0]?.store?.identifier || 'STORE';
        // const orderCashTotal = getOrderPhysicalCash(order);

        const mergedItemsMap = new Map();
        const meta = { physicalCash:0, collected: 0, credit: 0, cashiers: new Set(), dates: new Set() };

        orders.forEach(order => {
            const orderPhysicalCash = getOrderPhysicalCash(order);

            meta.physicalCash += Number(orderPhysicalCash || 0);
            meta.collected += Number(order.summary_collected || 0);
            meta.credit += Number(order.summary_credit || 0);

            meta.dates.add(new Date(order.placed_on).toLocaleDateString());
            const fname = order.creator?.user?.first_name?.trim();
            if (fname) meta.cashiers.add(fname);

            order.orderitems.forEach(item => {
                const key = item.catentry_id;
                const existing = mergedItemsMap.get(key) || { name: item.catentry.productname, qty: 0, total: 0 };
                mergedItemsMap.set(key, {
                    ...existing,
                    qty: existing.qty + item.quantity,
                    total: existing.total + item.totalproduct
                });
            });
        });

        const cashierArray = Array.from(meta.cashiers);
        const cashierDisplay = cashierArray.length <= 2
            ? cashierArray.join(' & ')
            : `${cashierArray[0]} & ${cashierArray[1]} +${cashierArray.length - 2}`;

        const lines = [
            ` ${storeName.toUpperCase().padEnd(32).slice(0, 32)}`,
            ` EOD COMBINED RECEIPT     ${new Date().toLocaleDateString()}`,
            ` Cashier: ${cashierDisplay}`,
            ` Orders:  ${orders.length} | ${Array.from(meta.dates).join(', ')}`,
            "=".repeat(42),

            ...Array.from(mergedItemsMap.values()).map(item => {
                const unitPriceStr = item.qty > 0 ? (item.total / item.qty).toLocaleString() : '—';
                return `${item.name.slice(0, 21).padEnd(21)} x${item.qty.toString().padStart(3)} @ ${currency}${unitPriceStr}\n             → ${currency}${item.total.toLocaleString()}`;
            }),

            "=".repeat(42),
            ` TOTAL PAID:           ${currency}${meta.collected.toLocaleString().padStart(14)}`,
            ` TOTAL PHYSICAL CASH:  ${currency}${meta.physicalCash.toLocaleString().padStart(14)}`,
            ` TOTAL CREDIT:         ${currency}${meta.credit.toLocaleString().padStart(14)}`,
            ` GRAND TOTAL:          ${currency}${(meta.collected + meta.credit).toLocaleString().padStart(14)}`,
            " ".repeat(42),
            "         THANK YOU • COME AGAIN"
        ];

        window.location.href = `rawbt://print?text=${encodeURIComponent(lines.join('\n'))}`;
    };

    const printPaymentSummary = (orders) => {
        if (!orders || orders.length === 0) {
            showToast("No orders selected", "warning");
            return;
        }

        const currency = orders[0]?.order_currency?.setcnote || '₦';
        const storeName = (orders[0]?.store?.identifier || 'STORE').toUpperCase();
        let totalColl = 0;
        let totalCred = 0;
        let totalPhysical = 0;

        const lines = [
            storeName.padEnd(40).slice(0, 40),
            "SALES & SETTLEMENT SUMMARY".padEnd(40),
            new Date().toLocaleString(),
            "-".repeat(42),
            "Order ID     Paid (Collected)      Credit",
            "-".repeat(42)
        ];

        
        orders.forEach(o => {
            const coll = Number(o.summary_collected || 0);
            const cred = Number(o.summary_credit || 0);
            const orderPhysicalCash = getOrderPhysicalCash(o);

            totalColl += coll;
            totalCred += cred;
            totalPhysical += orderPhysicalCash;

            const idStr = `#${o.orders_id}`.padEnd(12);
            const collStr = coll.toLocaleString().padStart(12);
            const credStr = cred.toLocaleString().padStart(14);

            lines.push(`${idStr}${collStr}${credStr}`);
        });

        const summaryLines = [
            "-".repeat(42),
            `TOTAL COLLECTED:       ${currency}${totalColl.toLocaleString().padStart(12)}`,
            `TOTAL CREDIT:          ${currency}${totalCred.toLocaleString().padStart(12)}`,
            `TOTAL PHYSICAL CASH:   ${currency}${totalPhysical.toLocaleString().padStart(12)}`,
            "=".repeat(42),
            `OVERALL SALES:         ${currency}${(totalColl + totalCred).toLocaleString().padStart(12)}`,
            " ".repeat(42),
            "          REPORT COMPLETE"
        ];

        const finalReceipt = [...lines, ...summaryLines].join('\n');

        window.location.href = `rawbt://print?text=${encodeURIComponent(finalReceipt)}`;
    };

    const handleBatchPrint = () => {
        const ordersToPrint = filteredOrders.filter(o => selectedIds.includes(o.orders_id));
        printThermalRawBT(ordersToPrint);
    };

    const loadSalesData = async (targetDate, storeId = selectedStoreId) => {
        if (!storeId) return;

        setLoading(true);
        setError(null);
        try {
            const data = await fetchEndOfDay({
                store_id: storeId,
                target_date: targetDate
            });
            console.log(data) // end of day original data
            setAllOrders(data);
        } catch (err) {
            const msg = err.response?.data?.detail || "Failed to fetch sales data.";
            setError(msg);
            showToast(msg, 'error');
        } finally {
            setLoading(false);
        }
    };

    const initializeStoresAndData = async (parsedUser) => {
        try {
            const fetchedStores = await fetchStoresWhereStaffAssigned(parsedUser);
            setStores(fetchedStores.map(store => ({
                label: store.identifier,
                value: store.store_id,
            })));
            if (fetchedStores.length > 0) {
                const defaultStoreId = fetchedStores[0].store_id;
                setSelectedStoreId(defaultStoreId);
                loadSalesData(reportDate, defaultStoreId);
            }
        } catch (err) {
            console.error(err)
            showToast(err.response?.data?.detail || "Failed to load assigned stores.", "error");
        }
    };

    useEffect(() => {
        const lcUser = localStorage.getItem("user");
        if (!lcUser) {
            showToast("Please log in to view sales reports.", "info");
            navigate('/login');
        } else {
            const parsedUser = JSON.parse(lcUser);
            setUser(parsedUser);
            // loadSalesData(reportDate);
            initializeStoresAndData(parsedUser);
        }
    }, []);

    const filteredOrders = useMemo(() => {
        return allOrders.filter(order => {
            if (selectedCreator !== 'all') {
                const creatorName = `${order.creator?.user?.first_name || ''} ${order.creator?.user?.last_name || ''}`.trim();
                if (creatorName !== selectedCreator) return false;
            }

            if (selectedCustomer !== 'all') {
                const customerName = `${order.customer?.user?.first_name || ''} ${order.customer?.user?.last_name || ''}`.trim();
                if (customerName !== selectedCustomer) return false;
            }

            if (searchTerm.trim()) {
                const lowerSearch = searchTerm.toLowerCase();
                const matchesKey = order.idempotency_key?.toLowerCase().includes(lowerSearch);
                const matchesProduct = order.orderitems.some(item =>
                    item.catentry?.productname?.toLowerCase().includes(lowerSearch)
                );
                if (!matchesKey && !matchesProduct) return false;
            }

            return true;
        });
    }, [allOrders, selectedCreator, selectedCustomer, searchTerm]);

    const physicalCashSummary = useMemo(() => {
        let total = 0;
        let totalreceipts = 0;
        filteredOrders.forEach(order => {
            if (!order.transactions) return;
            order.transactions.forEach(tx => {
                if (tx.transaction_type === "SALE_PRODUCT_RETAIL_CASH" && tx.postings) {
                    totalreceipts += 1;
                    tx.postings.forEach(posting => {
                        if (posting.posting_type === "debit") {
                            total += parseFloat(posting.amount || 0);
                        }
                    });
                }
            });
        });
        return {total, totalreceipts};
    }, [filteredOrders]);

    const getOrderPhysicalCash = (order) => {
        let total = 0;
        if (!order.transactions) return total;
        order.transactions.forEach(tx => {
            if(tx.transaction_type == "SALE_PRODUCT_RETAIL_CASH" && tx.postings) {
                tx.postings.forEach(posting => {
                    if(posting.posting_type == "debit") {
                        total += parseFloat(posting.amount || 0);
                    }
                });
            }
        });
        return total;
    };

    const stats = useMemo(() => {
        return filteredOrders.reduce((acc, order) => {
            const credit = Number(order.summary_credit || 0);
            const collected = Number(order.summary_collected || 0);
            const gross = Number(order.totalproduct || 0);
            const discount = Number(order.totaldiscount || 0);
            const netTotal = gross - discount

            acc.cashTotal += collected;
            acc.creditTotal += credit;
            acc.grandTotal += netTotal;

            if (collected > 0) acc.cashCount += 1;
            if (credit > 0) acc.creditCount += 1;

            return acc;
        }, { cashTotal: 0, creditTotal: 0, grandTotal: 0, cashCount: 0, creditCount: 0 });
    }, [filteredOrders]);

    const creatorsList = useMemo(() => {
        const unique = new Map();
        allOrders.forEach(order => {
            const name = `${order.creator?.user?.first_name || ''} ${order.creator?.user?.last_name || ''}`.trim();
            if (name) unique.set(name, name);
        });
        return ['all', ...Array.from(unique.values()).sort()];
    }, [allOrders]);

    const customersList = useMemo(() => {
        const unique = new Map();
        allOrders.forEach(order => {
            const name = `${order.customer?.user?.first_name || ''} ${order.customer?.user?.last_name || ''}`.trim();
            if (name) unique.set(name, name);
        });
        return ['all', ...Array.from(unique.values()).sort()];
    }, [allOrders]);

    const getPaymentTypeLabel = (order) => {
        const method = order.paymethod?.toLowerCase();

        if (method === 'cash') return { label: 'Cash Receipt', color: 'bg-emerald-100 text-emerald-800' };
        if (method === 'card') return { label: 'Card Payment', color: 'bg-blue-100 text-blue-800' };
        if (method === 'transfer') return { label: 'Bank Transfer', color: 'bg-indigo-100 text-indigo-800' };
        if (method === 'credit') return { label: 'Credit Invoice', color: 'bg-orange-100 text-orange-800' };
        if (method === 'split') return { label: 'Split Sale', color: 'bg-purple-100 text-purple-800' };
        return { label: order.status === 'OUT' ? 'Cash Receipt' : 'Credit Invoice', color: 'bg-gray-100 text-gray-800' };
    };

    const handleRefresh = () => {
        loadSalesData(reportDate);
    };

    const printThermalReceiptRawBT = (order) => {
        if (!order) return;

        const currency = order.order_currency?.setcnote || '₦';
        const store = (order.store?.identifier || 'STORE').toUpperCase();
        const dt = new Date(order.placed_on).toLocaleString();
        const ref = order.idempotency_key || order.orders_id;

        const cashier = `${order.creator?.user?.first_name || ''} ${order.creator?.user?.last_name || ''}`.trim() || '—';
        const customer = `${order.customer?.user?.first_name || ''} ${order.customer?.user?.last_name || ''}`.trim() || '—';

        const cash = Number(order.summary_collected || 0);
        const credit = Number(order.summary_credit || 0);
        const total = Number(order.totalproduct || 0);

        const status = credit > 0 ? 'PARTIAL / CREDIT' : 'PAID IN FULL';

        const lines = [
            store.padEnd(42).slice(0, 42),
            "SALES RECEIPT".padEnd(42),
            dt,
            ref ? `Ref: ${ref}` : '',
            "-".repeat(42),
            "",

            ...(order.orderitems || []).map(item => {
                const name = (item.catentry?.productname || 'Item').slice(0, 24).padEnd(24);
                const qty = item.quantity.toString().padStart(3);
                const price = Number(item.price || 0).toLocaleString();
                const amt = Number(item.totalproduct || 0).toLocaleString();
                return [
                    `${name} x${qty}`,
                    `      @ ${currency}${price}  → ${currency}${amt}`
                ];
            }).flat(),

            "-".repeat(42),
            `TOTAL PAYABLE     ${currency}${total.toLocaleString().padStart(14)}`,
            `CASH RECEIVED     ${currency}${cash.toLocaleString().padStart(14)}`,
            credit > 0 ? `CREDIT BALANCE    ${currency}${credit.toLocaleString().padStart(14)}` : '',
            "=".repeat(42),
            `Attendant: ${cashier}`,
            `Customer : ${customer}`,
            `Status   : ${status}`,
            "",
            "*** THANK YOU ***".padStart(21),
            "".padEnd(42),
        ].filter(Boolean);

        const text = lines.join('\n');
        window.location.href = `rawbt://print?text=${encodeURIComponent(text)}`;
    };

    if (!user) return null;

    return (
        <div className="flex flex-col min-h-screen bg-gray-50 pb-6 sm:pb-10">
            {/* Header + filters */}
            <div className="px-4 sm:px-6 pt-6 pb-4 bg-white border-b border-gray-200">
                <div className="flex flex-col gap-5">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                        <div>
                            <h1 className="text-xl sm:text-2xl font-bold text-gray-900 flex items-center gap-2">
                                <FileText className="text-blue-600" size={24} />
                                End of Day Sales Report
                            </h1>
                            <p className="text-sm text-gray-500 mt-1">
                                Daily summary of receipts and credit invoices
                            </p>
                        </div>

                        <div className="flex flex-wrap items-center gap-3">
                            <div className="flex items-center gap-2 bg-gray-50 p-2 rounded-lg border w-full sm:w-auto">
                                <Calendar size={18} className="text-gray-400" />
                                <span className="text-sm font-medium text-gray-600 whitespace-nowrap">
                                    Date:
                                </span>
                                <input
                                    type="date"
                                    className="bg-transparent border-none focus:ring-0 text-sm font-bold text-gray-800 flex-1"
                                    value={reportDate}
                                    onChange={(e) => {
                                        setReportDate(e.target.value);
                                        loadSalesData(e.target.value);
                                    }}
                                />
                            </div>

                            <div className="flex items-center gap-2 bg-gray-50 p-2 rounded-lg border w-full sm:w-auto">
                                <Store size={18} className="text-gray-400" />
                                <select
                                    className="bg-transparent border-none focus:ring-0 text-sm font-bold text-gray-800 flex-1"
                                    value={selectedStoreId || ''}
                                    onChange={(e) => {
                                        const newStoreId = e.target.value;
                                        setSelectedStoreId(newStoreId);
                                        loadSalesData(reportDate, newStoreId);
                                    }}
                                >
                                    {stores.map(s => (
                                        <option key={s.value} value={s.value}>{s.label}</option>
                                    ))}
                                </select>
                            </div>

                            <Button
                                onClick={handleRefresh}
                                variant="outline"
                                size="sm"
                                className="flex items-center gap-2 w-full sm:w-auto justify-center"
                                disabled={loading}
                            >
                                <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
                                Refresh
                            </Button>
                        </div>
                    </div>

                    <div className="flex flex-col sm:flex-row gap-3">
                        <Input
                            placeholder="Search order ID or product..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="w-full"
                        />

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 w-full sm:w-auto sm:min-w-[320px]">
                            <SearchableSelectBox
                                name="creator"
                                placeholder="All Cashiers"
                                value={selectedCreator}
                                onChange={(e) => setSelectedCreator(e.target.value)}
                                options={creatorsList.map(c => ({
                                    value: c,
                                    label: c === 'all' ? 'All Cashiers' : c
                                }))}
                                isClearable={true}
                            />

                            <SearchableSelectBox
                                name="customer"
                                placeholder="All Customers"
                                value={selectedCustomer}
                                onChange={(e) => setSelectedCustomer(e.target.value)}
                                options={customersList.map(c => ({
                                    value: c,
                                    label: c === 'all' ? 'All Customers' : c
                                }))}
                                isClearable={true}
                            />
                        </div>
                    </div>
                </div>
            </div>

            <div className="px-4 sm:px-6 py-6 space-y-6">
                {/* Summary cards */}
                <div className="grid grid-cols-1 xs:grid-cols-2 lg:grid-cols-4 gap-4">
                    <SummaryCard
                        title="Total Sales"
                        value={stats.cashTotal + stats.creditTotal}
                        icon={<TrendingUp className="text-blue-600" />}
                        color="bg-blue-50"
                        />
                    <SummaryCard
                        title="Physical Cash"
                        value={physicalCashSummary.total}
                        icon={<Banknote className="text-indigo-600" />}
                        color="bg-indigo-50"
                        count={physicalCashSummary.totalreceipts}
                        />
                    <SummaryCard
                        title="Cash + Bank"
                        value={stats.cashTotal}
                        count={stats.cashCount}
                        icon={<Wallet className="text-emerald-600" />}
                        color="bg-emerald-50"
                    />
                    <SummaryCard
                        title="Credit Invoices"
                        value={stats.creditTotal}
                        count={stats.creditCount}
                        icon={<CreditCard className="text-orange-600" />}
                        color="bg-orange-50"
                    />
                    {/* <SummaryCard
                        title="Total Orders"
                        value={filteredOrders.length}
                        isCurrency={false}
                        icon={<ShoppingBag className="text-purple-600" />}
                        color="bg-purple-50"
                    /> */}
                </div>

                {/* Main list - cards on mobile, table on desktop */}
                <Card className="overflow-hidden border-none shadow-sm">
                    {loading ? (
                        <div className="p-12 sm:p-20 flex flex-col items-center justify-center">
                            <Loader2 className="animate-spin h-10 w-10 text-blue-500 mb-4" />
                            <p className="text-gray-500">Generating Report...</p>
                        </div>
                    ) : filteredOrders.length === 0 ? (
                        <div className="p-12 sm:p-20 flex flex-col items-center justify-center text-center">
                            <div className="bg-gray-100 p-4 rounded-full mb-4">
                                <Info className="text-gray-400" size={32} />
                            </div>
                            <h3 className="text-lg font-medium text-gray-900">No transactions recorded</h3>
                            <p className="text-gray-500 mt-2">
                                No sales records for {new Date(reportDate).toLocaleDateString()}.
                            </p>
                        </div>
                    ) : (
                        <div className="divide-y divide-gray-100">

                            {/* THIS SECTION DISAPPEARS ON MOBILE PHONES. */}
                            {/* {selectedIds.length > 0 && (
                                <div className="p-4 bg-gray-50 border-b sticky top-0 z-10">
                                    <Dropdown
                                        trigger={
                                            <button className="flex items-center gap-2 bg-emerald-600 text-white px-5 py-2.5 rounded-lg font-medium shadow hover:bg-emerald-700 transition-colors w-full justify-center">
                                                <Printer size={18} />
                                                Print Options ({selectedIds.length})
                                                <ChevronDown size={16} />
                                            </button>
                                        }
                                        align="left"
                                        className="w-72 sm:w-80"
                                    >
                                        <div className="py-2">
                                            <div className="px-4 py-2 text-xs font-bold text-gray-400 uppercase tracking-wider">Thermal (RawBT)</div>
                                            <button className="w-full text-left px-4 py-2.5 text-sm hover:bg-gray-100 flex items-center gap-2"
                                                onClick={() => printThermalRawBT(filteredOrders.filter(o => selectedIds.includes(o.orders_id)))}>
                                                <Printer size={16} className="text-gray-500" /> Combined Items Receipt
                                            </button>
                                            <button className="w-full text-left px-4 py-2.5 text-sm hover:bg-gray-100 flex items-center gap-2"
                                                onClick={() => printPaymentSummary(filteredOrders.filter(o => selectedIds.includes(o.orders_id)))}>
                                                <CreditCard size={16} className="text-gray-500" /> Payment & Split Summary
                                            </button>

                                            <div className="my-2 border-t border-gray-100"></div>

                                            <div className="px-4 py-2 text-xs font-bold text-gray-400 uppercase tracking-wider">Preview & Export</div>
                                            <button className="w-full text-left px-4 py-2.5 text-sm hover:bg-blue-50 text-blue-700 flex items-center gap-2"
                                                onClick={() => handlePDFPreview(filteredOrders.filter(o => selectedIds.includes(o.orders_id)), false)}>
                                                <FileText size={16} /> Preview / Save PDF (Items)
                                            </button>
                                            <button className="w-full text-left px-4 py-2.5 text-sm hover:bg-blue-50 text-blue-700 flex items-center gap-2"
                                                onClick={() => handlePDFPreview(filteredOrders.filter(o => selectedIds.includes(o.orders_id)), true)}>
                                                <FileText size={16} /> Preview / Save PDF (Payments)
                                            </button>
                                        </div>
                                    </Dropdown>
                                </div>
                            )} */}

                            {/* WE REPLACE THE DISAPPEARING HEADER WITH THIS ONE */}
                            <div className="p-4 bg-gray-50 border-b sticky top-0 z-10 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                                {/* Global Mobile Select All Controller */}
                                <div className="flex items-center gap-3 sm:hidden pb-2 border-b border-gray-200 sm:border-0 sm:pb-0">
                                    <input
                                        type="checkbox"
                                        id="mobile-select-all"
                                        checked={filteredOrders.length > 0 && selectedIds.length === filteredOrders.length}
                                        onChange={toggleAll}
                                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 h-5 w-5"
                                    />
                                    <label htmlFor="mobile-select-all" className="text-sm font-semibold text-gray-700 select-none">
                                        {selectedIds.length === filteredOrders.length ? "Deselect All Orders" : "Select All Orders"}
                                    </label>
                                </div>

                                {/* Dynamic Action Trigger Dropdown */}
                                {selectedIds.length > 0 ? (
                                    <div className="w-full sm:w-auto">
                                        <Dropdown
                                            trigger={
                                                <button className="flex items-center gap-2 bg-emerald-600 text-white px-5 py-2.5 rounded-lg font-medium shadow hover:bg-emerald-700 transition-colors w-full sm:w-auto justify-center">
                                                    <Printer size={18} />
                                                    Print Options ({selectedIds.length})
                                                    <ChevronDown size={16} />
                                                </button>
                                            }
                                            align="left"
                                            className="w-full sm:w-80"
                                        >
                                            <div className="py-2">
                                                <div className="px-4 py-2 text-xs font-bold text-gray-400 uppercase tracking-wider">Thermal (RawBT)</div>
                                                <button className="w-full text-left px-4 py-2.5 text-sm hover:bg-gray-100 flex items-center gap-2"
                                                    onClick={() => printThermalRawBT(filteredOrders.filter(o => selectedIds.includes(o.orders_id)))}>
                                                    <Printer size={16} className="text-gray-500" /> Combined Items Receipt
                                                </button>
                                                <button className="w-full text-left px-4 py-2.5 text-sm hover:bg-gray-100 flex items-center gap-2"
                                                    onClick={() => printPaymentSummary(filteredOrders.filter(o => selectedIds.includes(o.orders_id)))}>
                                                    <CreditCard size={16} className="text-gray-500" /> Payment & Split Summary
                                                </button>

                                                <div className="my-2 border-t border-gray-100"></div>

                                                <div className="px-4 py-2 text-xs font-bold text-gray-400 uppercase tracking-wider">Preview & Export</div>
                                                <button className="w-full text-left px-4 py-2.5 text-sm hover:bg-blue-50 text-blue-700 flex items-center gap-2"
                                                    onClick={() => handlePDFPreview(filteredOrders.filter(o => selectedIds.includes(o.orders_id)), false)}>
                                                    <FileText size={16} /> Preview / Save PDF (Items)
                                                </button>
                                                <button className="w-full text-left px-4 py-2.5 text-sm hover:bg-blue-50 text-blue-700 flex items-center gap-2"
                                                    onClick={() => handlePDFPreview(filteredOrders.filter(o => selectedIds.includes(o.orders_id)), true)}>
                                                    <FileText size={16} /> Preview / Save PDF (Payments)
                                                </button>
                                            </div>
                                        </Dropdown>
                                    </div>
                                ) : (
                                    <div className="hidden sm:block text-xs font-medium text-gray-500">
                                        Select items below to perform batch actions
                                    </div>
                                )}
                            </div>

                            {/* Mobile: Card view */}
                            <div className="block sm:hidden divide-y divide-gray-100">
                                {filteredOrders.map((order) => {
                                    const { label, color } = getPaymentTypeLabel(order);
                                    const isSelected = selectedIds.includes(order.orders_id);

                                    return (
                                        <div
                                            key={order.orders_id}
                                            className={`p-4 hover:bg-gray-50 active:bg-gray-100 transition-colors cursor-pointer ${isSelected ? 'bg-blue-50' : ''}`}
                                            onClick={() => handleViewReceipt(order)}
                                        >
                                            <div className="flex items-start justify-between gap-3">
                                                <div className="flex items-start gap-3 flex-1" onClick={(e) => e.stopPropagation()}>
                                                    <input
                                                        type="checkbox"
                                                        checked={isSelected}
                                                        onChange={() => toggleOrder(order.orders_id)}
                                                        onClick={(e) => e.stopPropagation()}
                                                        className="mt-1.5 rounded border-gray-300 text-blue-600 focus:ring-blue-500 h-5 w-5"
                                                    />
                                                    <div>
                                                        <div className="font-bold text-gray-900 text-base">#{order.orders_id}</div>
                                                        <div className="text-sm text-gray-600 mt-0.5">
                                                            {new Date(order.placed_on).toLocaleString([], {
                                                                dateStyle: 'medium',
                                                                timeStyle: 'short'
                                                            })}
                                                        </div>
                                                    </div>
                                                </div>

                                                <div className="text-right shrink-0">
                                                    <div className="text-lg font-bold text-gray-900">
                                                        {order.order_currency?.setcnote}{Number(order.totalproduct).toLocaleString()}
                                                    </div>
                                                    <span className={`inline-flex items-center px-2.5 py-0.5 mt-1 rounded-full text-xs font-medium ${color}`}>
                                                        {label}
                                                    </span>
                                                </div>
                                            </div>

                                            <div className="mt-3 text-sm">
                                                <div className="font-medium text-gray-800">
                                                    {order.customer?.user?.first_name || ''} {order.customer?.user?.last_name || '—'}
                                                </div>
                                                <div className="text-gray-600 text-xs mt-0.5">
                                                    {order.orderitems.length} item{order.orderitems.length !== 1 ? 's' : ''} • {order.store?.identifier || '—'}
                                                </div>
                                            </div>

                                            {order.status === 'HLD' && (
                                                <div className="mt-2 text-xs text-orange-600 font-medium">
                                                    Due: {order.creditsale?.payment_due_date || '—'}
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>

                            {/* Desktop/tablet: Table view */}
                            <div className="hidden sm:block overflow-x-auto">
                                <table className="w-full text-left border-collapse text-sm min-w-[720px]">
                                    <thead>
                                        <tr className="bg-gray-50 border-b border-gray-200">
                                            <th className="px-6 py-4">
                                                <input
                                                    type="checkbox"
                                                    checked={selectedIds.length === filteredOrders.length && filteredOrders.length > 0}
                                                    onChange={toggleAll}
                                                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                                />
                                            </th>
                                            <th className="px-6 py-4 text-xs font-semibold text-gray-600 uppercase">Order ID / Time</th>
                                            <th className="px-6 py-4 text-xs font-semibold text-gray-600 uppercase">Customer</th>
                                            <th className="px-6 py-4 text-xs font-semibold text-gray-600 uppercase">Items</th>
                                            <th className="px-6 py-4 text-xs font-semibold text-gray-600 uppercase">Type</th>
                                            <th className="px-6 py-4 text-xs font-semibold text-gray-600 uppercase text-right">Amount</th>
                                            <th className="px-6 py-4 text-xs font-semibold text-gray-600 uppercase text-right">Discount</th>
                                            <th className="px-6 py-4 text-xs font-semibold text-gray-600 uppercase text-center">Action</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-100 bg-white">
                                        {filteredOrders.map((order) => {
                                            const { label, color } = getPaymentTypeLabel(order);

                                            return (
                                                <tr
                                                    key={order.orders_id}
                                                    className="hover:bg-gray-50 transition-colors cursor-pointer"
                                                    onClick={() => handleViewReceipt(order)}
                                                >
                                                    <td className="px-6 py-4" onClick={(e) => e.stopPropagation()}>
                                                        <input
                                                            type="checkbox"
                                                            checked={selectedIds.includes(order.orders_id)}
                                                            onChange={() => toggleOrder(order.orders_id)}
                                                            onClick={(e) => e.stopPropagation()}
                                                            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                                        />
                                                    </td>
                                                    <td className="px-6 py-4">
                                                        <div className="font-bold text-gray-900">#{order.orders_id}</div>
                                                        <div className="text-xs text-gray-500">
                                                            {new Date(order.placed_on).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                                        </div>
                                                    </td>
                                                    <td className="px-6 py-4">
                                                        <div className="text-sm font-medium text-gray-800">
                                                            {order.customer?.user?.first_name} {order.customer?.user?.last_name}
                                                        </div>
                                                        <div className="text-xs text-gray-500 italic">{order.store?.identifier}</div>
                                                    </td>
                                                    <td className="px-6 py-4">
                                                        <div className="text-sm text-gray-600">
                                                            {order.orderitems.length} item(s)
                                                        </div>
                                                        <div className="text-xs text-gray-500 truncate max-w-[180px]">
                                                            {order.orderitems.map(i => i.catentry?.productname).join(', ')}
                                                        </div>
                                                    </td>
                                                    <td className="px-6 py-4">
                                                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${color}`}>
                                                            {label}
                                                        </span>
                                                    </td>
                                                    <td className="px-6 py-4 text-right">
                                                        <div className="text-sm font-bold text-gray-900">
                                                            {order.order_currency?.setcnote}{Number(order.totalproduct).toLocaleString()}
                                                        </div>
                                                        {order.status === 'HLD' && (
                                                            <div className="text-xs text-orange-600 font-medium">
                                                                Due: {order.creditsale?.payment_due_date}
                                                            </div>
                                                        )}
                                                    </td>
                                                    <td className="px-6 py-4 text-right">
                                                        <div className="text-sm font-medium text-amber-600">
                                                            {order.order_currency?.setcnote}{Number(order.totaldiscount || 0).toLocaleString()}
                                                        </div>
                                                    </td>
                                                    <td className="px-6 py-4 text-center" onClick={(e) => e.stopPropagation()}>
                                                        <button
                                                            onClick={() => handleViewReceipt(order)}
                                                            className="text-indigo-600 hover:text-indigo-800 font-medium text-sm"
                                                        >
                                                            View
                                                        </button>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}
                </Card>
            </div>

            <Modal
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                title={`Receipt #${selectedOrder?.orders_id || ''}`}
                size="md"
            >
                <div className="max-h-[70vh] overflow-y-auto px-2 sm:px-0">
                    {selectedOrder && <ThermalReceipt order={selectedOrder} />}
                </div>

                <div className="mt-6 flex flex-col sm:flex-row gap-3 justify-center px-2">
                    <Button
                        onClick={() => window.print()}
                        variant="outline"
                        className="flex items-center justify-center gap-2 py-3 text-base sm:text-sm w-full sm:w-auto"
                    >
                        <Printer size={20} />
                        Browser Print
                    </Button>

                    <Button
                        onClick={() => printThermalReceiptRawBT(selectedOrder)}
                        className="bg-emerald-600 hover:bg-emerald-700 flex items-center justify-center gap-2 py-3 text-base sm:text-sm w-full sm:w-auto"
                    >
                        <Printer size={20} />
                        Thermal (RawBT)
                    </Button>
                </div>
            </Modal>

            {/* <TrialModal isOpen={showTrialModal} onRequestClose={() => setShowTrialModal(false)} /> */}
        </div>
    );
};

export default EndOfDaySalesReport;